<template>
  <div class="addresses-phones-container">
    <div v-if="user" class="flex flex-col gap-4">
      <InfoautoEditAddressModal
        v-if="showEditAddressModal && !isCostaRicaStore"
        :customerId="customerId"
        :address="selectedAddress"
        @address-updated="handleAddressUpdated"
        @close="showEditAddressModal = false"
      ></InfoautoEditAddressModal>
      <InfoautoEditAddressModalCostaRica
        v-if="showEditAddressModal && isCostaRicaStore"
        :customerId="customerId"
        :address="selectedAddress"
        @address-updated="handleAddressUpdated"
        @close="showEditAddressModal = false"
      ></InfoautoEditAddressModalCostaRica>
      <InfoautoAddressForm
        v-if="showAddAddressModal && !isCostaRicaStore"
        :customerName="user.generalInfo.name"
        :customerInfoautoId="customerId"
        @close="showAddAddressModal = false"
        @addressCreated="handleAddressCreated"
      ></InfoautoAddressForm>
      <InfoautoAddressFormCostaRica
        v-if="showAddAddressModal && isCostaRicaStore"
        :customerName="user.generalInfo.name"
        :customerInfoautoId="customerId"
        @close="showAddAddressModal = false"
        @addressCreated="handleAddressCreated"
      ></InfoautoAddressFormCostaRica>
      <InfoautoPhoneForm
        v-if="showAddPhoneModal"
        :customerName="user.generalInfo.name"
        :customerInfoautoId="customerId"
        @close="showAddPhoneModal = false"
        @phoneCreated="handlePhoneCreated"
      ></InfoautoPhoneForm>
      <InfoautoEditPhoneModal
        v-if="showEditPhoneModal"
        :customerId="customerId"
        :phone="selectedPhone"
        @phone-updated="handlePhoneUpdated"
        @close="showEditPhoneModal = false"
      ></InfoautoEditPhoneModal>

      <!-- Show disabled toggle -->
      <button
        v-if="hasDisabledItems"
        class="show-disabled-toggle"
        :class="{ active: showDisabled }"
        @click="$emit('toggle-disabled')"
      >
        <i class="mdi" :class="showDisabled ? 'mdi-eye-off' : 'mdi-eye'"></i>
        {{ showDisabled ? "Hide disabled" : "Show disabled" }}
      </button>

      <!-- Addresses Section -->
      <div class="section-box">
        <div class="section-header flex justify-between items-center mb-4">
          <h2 class="section-title">{{ $t("message.customer.addresses") }}</h2>
          <button
            @click="showAddAddressModal = true"
            class="btn-success-button flex items-center gap-2"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            {{ $t("message.customer.add_new_address") }}
          </button>
        </div>
        <div class="flex flex-row flex-wrap gap-2">
          <div
            v-for="address in filteredAddresses"
            :key="address.infoautoAddressId"
            class="p-4 border rounded-md relative address-card"
            :class="{
              'address-disabled': address.disabled == 1,
              'address-selectable': selectable && address.disabled !== 1,
              'is-current': isCurrentAddress(address)
            }"
            @click="
              selectable && address.disabled !== 1
                ? selectAddress(address)
                : null
            "
          >
            <!-- Action Buttons -->
            <div class="address-actions">
              <button
                @click.stop="editAddress(address)"
                class="action-btn edit-btn"
                title="Edit Address"
              >
                <img src="@/assets/icons/edit.svg" alt="Edit" class="w-4 h-4" />
              </button>
              <button
                v-if="address.disabled === 1"
                @click.stop="toggleAddressStatus(address, 0)"
                class="action-btn enable-btn"
                title="Enable Address"
              >
                ✓
              </button>
              <button
                v-else
                @click.stop="toggleAddressStatus(address, 1)"
                class="action-btn disable-btn"
                title="Disable Address"
              >
                ✕
              </button>
            </div>

            <div v-if="isCurrentAddress(address)" class="current-badge">
              <i class="mdi mdi-check-circle"></i>
              {{ $t("message.order.current_in_order") }}
            </div>
            <InfoautoAddressDisplay
              :address="address"
              :showResidential="false"
              :statusLabel="$t('message.customer.status')"
              :enabledLabel="$t('message.customer.enabled')"
              :disabledLabel="$t('message.customer.disabled')"
            />
          </div>
          <div v-if="filteredAddresses.length === 0" class="text-gray-500 p-4">
            {{ $t("message.customer.no_addresses") }}
          </div>
        </div>
      </div>

      <!-- Phones Section -->
      <div class="section-box">
        <div class="section-header flex justify-between items-center mb-4">
          <h2 class="section-title">{{ $t("message.customer.phones") }}</h2>
          <button
            @click="showAddPhoneModal = true"
            class="btn-success-button flex items-center gap-2"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            {{ $t("message.customer.add_new_phone") }}
          </button>
        </div>
        <div class="flex flex-row flex-wrap gap-2">
          <div
            v-for="phone in filteredPhones"
            :key="phone.infoautoPhoneId"
            class="p-4 border rounded-md relative phone-card"
            :class="{
              'phone-disabled': phone.disabled == 1,
              'phone-selectable': selectable && phone.disabled !== 1,
              'is-current': isCurrentPhone(phone)
            }"
            @click="selectable && phone.disabled !== 1 && selectPhone(phone)"
          >
            <!-- Action Buttons -->
            <div class="phone-actions">
              <button
                @click.stop="editPhone(phone)"
                class="action-btn edit-btn"
                title="Edit Phone"
              >
                <img src="@/assets/icons/edit.svg" alt="Edit" class="w-4 h-4" />
              </button>
              <button
                v-if="phone.disabled === 1"
                @click.stop="togglePhoneStatus(phone, 0)"
                class="action-btn enable-btn"
                title="Enable Phone"
              >
                ✓
              </button>
              <button
                v-else
                @click.stop="togglePhoneStatus(phone, 1)"
                class="action-btn disable-btn"
                title="Disable Phone"
              >
                ✕
              </button>
            </div>

            <div class="phone-content">
              <div v-if="isCurrentPhone(phone)" class="current-badge">
                <i class="mdi mdi-check-circle"></i>
                {{ $t("message.order.current_in_order") }}
              </div>
              <div class="phone-number">
                <svg
                  class="w-5 h-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span class="text-lg font-semibold">
                  {{ phone.areaCode ? `(${phone.areaCode}) ` : ""
                  }}{{ phone.number }}
                </span>
              </div>
              <p class="text-sm text-gray-600">
                ID: {{ phone.infoautoPhoneId }}
              </p>
              <p
                class="text-sm"
                :class="
                  phone.disabled === 1 ? 'text-red-600' : 'text-green-600'
                "
              >
                {{
                  phone.disabled === 1
                    ? $t("message.customer.disabled")
                    : $t("message.customer.active")
                }}
              </p>
            </div>
          </div>
          <div v-if="filteredPhones.length === 0" class="text-gray-500 p-4">
            {{ $t("message.customer.no_phones") }}
          </div>
        </div>
      </div>
    </div>
    <div
      v-else-if="loading"
      class="h-60 flex flex-col items-center justify-center"
    >
      <baseMiniSpinner></baseMiniSpinner>
    </div>
  </div>
</template>
<script>
import InfoautoEditAddressModal from "../customer/InfoautoEditAddressModal.vue";
import InfoautoEditAddressModalCostaRica from "../customer/InfoautoEditAddressModalCostaRica.vue";
import InfoautoEditPhoneModal from "../customer/InfoautoEditPhoneModal.vue";
import InfoautoAddressForm from "../Infoauto/InfoautoAddressForm.vue";
import InfoautoAddressFormCostaRica from "../Infoauto/InfoautoAddressFormCostaRica.vue";
import InfoautoPhoneForm from "../Infoauto/InfoautoPhoneForm.vue";
import InfoautoAddressDisplay from "@/components/shared/InfoautoAddressDisplay.vue";
import {
  infoautoGetSingleCustomer,
  updateAddressToCustomer,
  updatePhoneToCustomer
} from "@/api/store/Infoauto.js";
import {
  isOrderAddress,
  isOrderPhone,
  orderFirst
} from "@/utils/order-address-match";

export default {
  components: {
    InfoautoEditAddressModal,
    InfoautoEditAddressModalCostaRica,
    InfoautoEditPhoneModal,
    InfoautoAddressForm,
    InfoautoAddressFormCostaRica,
    InfoautoPhoneForm,
    InfoautoAddressDisplay
  },
  props: {
    customerId: {
      type: String,
      required: true
    },
    selectable: {
      type: Boolean,
      default: false
    },
    showDisabled: {
      type: Boolean,
      default: false
    },
    // The order's address (with its phone), when picking for an order:
    // marked and listed first
    currentAddress: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      showEditAddressModal: false,
      showAddAddressModal: false,
      showAddPhoneModal: false,
      showEditPhoneModal: false,
      selectedAddress: null,
      selectedPhone: null,
      user: null,
      loading: true,
      error: false
    };
  },
  computed: {
    userCanEditInfoautoCustomer() {
      return this.$store.getters["auth/userHasPermission"](
        "can_edit_infoauto_customer"
      );
    },
    isCostaRicaStore() {
      const activeStore = this.$store.getters["general/getActiveStore"];
      return activeStore?.code === "nkr_costa_rica";
    },
    filteredAddresses() {
      if (!this.user?.address) return [];
      const addresses = this.showDisabled
        ? this.user.address
        : this.user.address.filter(addr => addr.disabled !== 1);
      return orderFirst(addresses, this.isCurrentAddress);
    },
    filteredPhones() {
      if (!this.user?.phone) return [];
      const phones = this.showDisabled
        ? this.user.phone
        : this.user.phone.filter(phone => phone.disabled !== 1);
      return orderFirst(phones, this.isCurrentPhone);
    },
    hasDisabledItems() {
      const disabledAddresses = (this.user?.address || []).some(
        a => a.disabled === 1
      );
      const disabledPhones = (this.user?.phone || []).some(
        p => p.disabled === 1
      );
      return disabledAddresses || disabledPhones;
    }
  },
  async mounted() {
    try {
      this.loadCustomer();
    } catch (error) {
      this.error = true;
    }
    this.loading = false;
  },
  methods: {
    isCurrentAddress(address) {
      return isOrderAddress(address, this.currentAddress);
    },
    isCurrentPhone(phone) {
      return isOrderPhone(phone, this.currentAddress);
    },
    async loadCustomer() {
      this.user = await infoautoGetSingleCustomer(this.customerId);
    },
    selectAddress(address) {
      if (this.selectable && address.disabled !== 1) {
        this.$emit("address-selected", address);
      }
    },
    selectPhone(phone) {
      if (this.selectable && phone.disabled !== 1) {
        this.$emit("phone-selected", phone);
      }
    },
    editAddress(address) {
      this.selectedAddress = address;
      this.showEditAddressModal = true;
    },
    handleAddressUpdated() {
      this.showEditAddressModal = false;
      this.selectedAddress = null;
      this.loadCustomer();
    },
    handleAddressCreated() {
      this.showAddAddressModal = false;
      this.loadCustomer();
    },
    handlePhoneCreated() {
      this.showAddPhoneModal = false;
      this.loadCustomer();
    },
    editPhone(phone) {
      this.selectedPhone = phone;
      this.showEditPhoneModal = true;
    },
    handlePhoneUpdated() {
      this.showEditPhoneModal = false;
      this.selectedPhone = null;
      this.loadCustomer();
    },
    async togglePhoneStatus(phone, newStatus) {
      try {
        const confirmed = confirm(
          newStatus === 1
            ? "Are you sure you want to disable this phone?"
            : "Are you sure you want to enable this phone?"
        );

        if (!confirmed) return;

        // Prepare the phone data with updated status
        const phoneData = {
          telefono_id: phone.infoautoPhoneId,
          numero: phone.number,
          cod_area: phone.areaCode || "",
          tipo_4: phone.tipo4 || 0,
          disabled: newStatus
        };

        console.log("Toggling phone status:", phoneData);

        await updatePhoneToCustomer(this.customerId, phoneData);

        // Reload customer data to reflect changes
        await this.loadCustomer();

        this.$store.dispatch(
          "alerts/addAlert",
          {
            type: "success",
            message: `Phone ${
              newStatus === 1 ? "disabled" : "enabled"
            } successfully`
          },
          { root: true }
        );
      } catch (error) {
        console.error("Error toggling phone status:", error);
        const errorMessage =
          error.response?.data?.message || "Failed to update phone status";
        this.$store.dispatch(
          "alerts/addAlert",
          {
            type: "error",
            message: errorMessage
          },
          { root: true }
        );
      }
    },
    async toggleAddressStatus(address, newStatus) {
      try {
        const confirmed = confirm(
          newStatus === 1
            ? "Are you sure you want to disable this address?"
            : "Are you sure you want to enable this address?"
        );

        if (!confirmed) return;

        // Prepare the full address data with updated status
        const addressData = {
          dir_id: address.infoautoAddressId,
          name: address.name || "",
          company: address.company || "",
          street1: address.street1,
          street2: address.street2 || null,
          street3: address.street3 || null,
          disabled: newStatus
        };

        // Add Costa Rica specific fields if store is Costa Rica
        if (this.isCostaRicaStore) {
          addressData.tipo_3 = 1;
          addressData.cod_provincia = address.codProvincia;
          addressData.cod_canton = address.codCanton;
          addressData.cod_distrito = address.codDistrito;
          addressData.cod_barrio = address.codBarrio || "0";
          addressData.provincia = address.provincia;
          addressData.canton = address.canton;
          addressData.distrito = address.distrito;
          addressData.barrio = address.barrio || " ";
          addressData.country = address.country;
          addressData.postal_code = address.postalCode || " ";
          addressData.residential = address.residential || 0;
        } else {
          // Standard address fields
          addressData.tipo_3 = address.tipo3 || 0;
          addressData.city = address.city;
          addressData.state = address.state;
          addressData.postalCode = address.postalCode;
          addressData.country = address.country;
          addressData.residential = address.residential || 0;
        }

        console.log("Toggling address status:", addressData);

        await updateAddressToCustomer(this.customerId, addressData);

        // Reload customer data to reflect changes
        await this.loadCustomer();

        this.$store.dispatch(
          "alerts/addAlert",
          {
            type: "success",
            message: `Address ${
              newStatus === 1 ? "disabled" : "enabled"
            } successfully`
          },
          { root: true }
        );
      } catch (error) {
        console.error("Error toggling address status:", error);
        const errorMessage =
          error.response?.data?.message || "Failed to update address status";
        this.$store.dispatch(
          "alerts/addAlert",
          {
            type: "error",
            message: errorMessage
          },
          { root: true }
        );
      }
    }
  }
};
</script>
<style lang="postcss" scoped>
.addresses-phones-container {
  width: 100%;
  padding: 10px;
}

.section-box {
  @apply p-4 border rounded-md bg-white;
}

.section-title {
  @apply font-bold text-xl text-black;
}

.address-card {
  position: relative;
  min-width: 280px;
  max-width: 320px;
  width: 100%;
}

.address-card p {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.address-disabled {
  @apply bg-gray-200 border-red-600 opacity-40;
}

.address-actions {
  @apply absolute top-2 right-2 flex gap-2;
}

.action-btn {
  @apply p-2 rounded-md cursor-pointer transition-colors;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.edit-btn {
  @apply bg-blue-500 hover:bg-blue-600 text-white;
}

.enable-btn {
  @apply bg-green-500 hover:bg-green-600 text-white font-bold;
}

.disable-btn {
  @apply bg-red-500 hover:bg-red-600 text-white font-bold;
}

.phone-card {
  position: relative;
  min-width: 250px;
  max-width: 320px;
  width: 100%;
}

.phone-disabled {
  @apply bg-gray-200 border-red-600 opacity-40;
}

.phone-actions {
  @apply absolute top-2 right-2 flex gap-2;
}

.phone-content {
  @apply flex flex-col gap-2;
}

.phone-number {
  @apply flex items-center gap-2;
}

.address-selectable,
.phone-selectable {
  cursor: pointer;
  transition: all 0.2s;
}

.address-selectable:hover,
.phone-selectable:hover {
  @apply bg-blue-50 border-blue-400;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Los botones de la tarjeta van encima del primer renglon: que el nombre no
   pase por debajo. La columna de etiqueta se calcula sobre el ancho sin ese
   padding para que el valor quede alineado con los demas renglones */
.address-card ::v-deep .address-display p:first-child {
  grid-template-columns: minmax(90px, calc((100% + 76px) * 0.32)) minmax(0, 1fr);
  padding-right: 76px;
}

/* Movil: entre modal, contenedor, seccion y tarjeta se iban ~56px por lado y
   las tarjetas tenian minimo 280px. La seccion pierde su caja (queda el
   titulo) y las tarjetas ocupan todo el ancho */
@media (max-width: 767px) {
  .addresses-phones-container {
    padding: 0;
  }

  .addresses-phones-container > div {
    gap: 1rem;
  }

  .section-box {
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
  }

  .section-header {
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .section-title {
    font-size: 1.125rem;
  }

  .section-header .btn-success-button {
    flex-shrink: 0;
    gap: 0.25rem;
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    white-space: nowrap;
  }

  .section-header .btn-success-button svg {
    width: 1rem;
    height: 1rem;
  }

  .address-card,
  .phone-card {
    min-width: 0;
    max-width: none;
    padding: 0.5rem 0.75rem;
  }

  .address-actions,
  .phone-actions {
    top: 0.375rem;
    right: 0.5rem;
  }

  .address-card ::v-deep .address-display p {
    padding: 0.2rem 0;
    font-size: 15px;
    line-height: 1.35;
  }

  .phone-content {
    gap: 0.125rem;
  }

  .show-disabled-toggle {
    margin-bottom: 0;
  }
}

/* La direccion o el telefono que ya tiene la orden */
.address-card.is-current,
.phone-card.is-current {
  border: 2px solid #16a34a;
  background: #f0fdf4;
}

.current-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  background: #16a34a;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.phone-content .current-badge {
  align-self: flex-start;
}

.show-disabled-toggle {
  @apply px-3 py-1.5 text-sm font-medium rounded-md border mb-3;
  @apply flex items-center gap-1.5 transition-all duration-200;
  background: white;
  color: #6b7280;
  border-color: #d1d5db;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }

  &.active {
    background: #fef3c7;
    color: #92400e;
    border-color: #fbbf24;
  }

  i {
    font-size: 16px;
  }
}
</style>
